import React, { useEffect, useState, useCallback } from 'react';
import {
  Alert, AlertIcon, Badge, Box, Button, Flex, FormControl, FormLabel,
  FormHelperText, Heading, HStack, Icon, IconButton, Input, InputGroup,
  InputRightElement, Modal, ModalBody, ModalCloseButton, ModalContent,
  ModalFooter, ModalHeader, ModalOverlay, Spinner, Switch, Table, Tbody,
  Td, Text, Th, Thead, Tooltip, Tr, useDisclosure, VStack, Tag, TagLabel,
  TagCloseButton, Wrap, WrapItem,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2, FiShield, FiEye, FiEyeOff, FiX } from 'react-icons/fi';

const EMPTY_FORM = {
  tenant_id: '',
  entra_tenant_id: '',
  entra_client_id: '',
  entra_client_secret: '',
  enabled: false,
  allowed_domains: [],
};

export default function EntraConfigs({ token, apiBase }) {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [configuredTenant, setConfiguredTenant] = useState('');
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [domainInput, setDomainInput] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose,
  } = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = { Authorization: `Token ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    setLoading(true);
    fetch(`${apiBase}/api/entra-configs/`, { headers })
      .then(r => r.json())
      .then(data => setConfigs(data.results || []))
      .catch(() => setError('Failed to load configs'))
      .finally(() => setLoading(false));
  }, [apiBase, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    fetch(`${apiBase}/api/config/`, { headers })
      .then(r => r.json())
      .then(data => setConfiguredTenant(data.tenant_id || ''))
      .catch(() => {});
  }, [apiBase, token]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setFormData(EMPTY_FORM);
    setEditId(null);
    setFormError('');
    setShowSecret(false);
    setDomainInput('');
    onOpen();
  };

  const openEdit = (config) => {
    setFormData({
      tenant_id: config.tenant_id,
      entra_tenant_id: config.entra_tenant_id,
      entra_client_id: config.entra_client_id,
      entra_client_secret: config.entra_client_secret, // '***' or ''
      enabled: config.enabled,
      allowed_domains: config.allowed_domains || [],
    });
    setEditId(config.id);
    setFormError('');
    setShowSecret(false);
    setDomainInput('');
    onOpen();
  };

  const addDomain = () => {
    const d = domainInput.trim().toLowerCase();
    if (!d) return;
    if (formData.allowed_domains.includes(d)) { setDomainInput(''); return; }
    setFormData(f => ({ ...f, allowed_domains: [...f.allowed_domains, d] }));
    setDomainInput('');
  };

  const removeDomain = (domain) => {
    setFormData(f => ({ ...f, allowed_domains: f.allowed_domains.filter(x => x !== domain) }));
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError('');
    try {
      const url = editId
        ? `${apiBase}/api/entra-configs/${editId}/`
        : `${apiBase}/api/entra-configs/`;
      const method = editId ? 'PATCH' : 'POST';
      const body = { ...formData };
      // Don't send placeholder secret on edit
      if (editId && body.entra_client_secret === '***') {
        delete body.entra_client_secret;
      }
      const res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) {
        setFormError(data.error || 'Save failed');
        return;
      }
      onClose();
      load();
    } catch {
      setFormError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = (config) => {
    setDeleteTarget(config);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await fetch(`${apiBase}/api/entra-configs/${deleteTarget.id}/`, {
      method: 'DELETE', headers,
    });
    onDeleteClose();
    load();
  };

  return (
    <Box p={8}>
      <Flex justify="space-between" align="center" mb={6}>
        <Box>
          <Heading size="lg" color="gray.800">Entra Configurations</Heading>
          <Text color="gray.500" mt={1}>
            Microsoft Entra SSO settings
            {configuredTenant && (
              <> for tenant <Badge colorScheme="purple" fontSize="sm" ml={1}>{configuredTenant}</Badge></>
            )}
          </Text>
        </Box>
        <Button leftIcon={<Icon as={FiPlus} />} colorScheme="blue" onClick={openCreate}>
          Add Config
        </Button>
      </Flex>

      {error && <Alert status="error" mb={4}><AlertIcon />{error}</Alert>}
      {loading && <Spinner size="xl" color="blue.500" />}

      {!loading && configs.length === 0 && (
        <Box textAlign="center" py={20} color="gray.400">
          <Icon as={FiShield} boxSize={12} mb={4} />
          <Text fontSize="lg">No Entra configurations yet.</Text>
          <Text fontSize="sm">Click "Add Config" to configure SSO for a tenant.</Text>
        </Box>
      )}

      {!loading && configs.length > 0 && (
        <Box bg="white" rounded="xl" shadow="sm" overflow="hidden">
          <Table variant="simple">
            <Thead bg="gray.50">
              <Tr>
                <Th>Tenant ID</Th>
                <Th>Entra Tenant ID</Th>
                <Th>Client ID</Th>
                <Th>Allowed Domains</Th>
                <Th>Status</Th>
                <Th>Updated</Th>
                <Th></Th>
              </Tr>
            </Thead>
            <Tbody>
              {configs.map(config => (
                <Tr key={config.id} _hover={{ bg: 'gray.50' }}>
                  <Td fontWeight="semibold" color="blue.700">{config.tenant_id}</Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600" fontFamily="mono" isTruncated maxW="160px">
                      {config.entra_tenant_id || '—'}
                    </Text>
                  </Td>
                  <Td>
                    <Text fontSize="sm" color="gray.600" fontFamily="mono" isTruncated maxW="160px">
                      {config.entra_client_id || '—'}
                    </Text>
                  </Td>
                  <Td>
                    <Wrap>
                      {(config.allowed_domains || []).map(d => (
                        <WrapItem key={d}>
                          <Badge colorScheme="teal" fontSize="xs">{d}</Badge>
                        </WrapItem>
                      ))}
                      {(!config.allowed_domains || config.allowed_domains.length === 0) && (
                        <Text color="gray.400" fontSize="sm">none</Text>
                      )}
                    </Wrap>
                  </Td>
                  <Td>
                    <Badge colorScheme={config.enabled ? 'green' : 'gray'}>
                      {config.enabled ? 'Enabled' : 'Disabled'}
                    </Badge>
                  </Td>
                  <Td fontSize="sm" color="gray.500">
                    {new Date(config.updated_at).toLocaleDateString()}
                  </Td>
                  <Td>
                    <HStack spacing={2}>
                      <Tooltip label="Edit">
                        <IconButton
                          icon={<FiEdit2 />} size="sm" variant="ghost"
                          colorScheme="blue" onClick={() => openEdit(config)}
                          aria-label="Edit"
                        />
                      </Tooltip>
                      <Tooltip label="Delete">
                        <IconButton
                          icon={<FiTrash2 />} size="sm" variant="ghost"
                          colorScheme="red" onClick={() => confirmDelete(config)}
                          aria-label="Delete"
                        />
                      </Tooltip>
                    </HStack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}

      {/* Create / Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editId ? 'Edit Entra Config' : 'Add Entra Config'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {formError && (
              <Alert status="error" mb={4} rounded="lg"><AlertIcon />{formError}</Alert>
            )}
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Selwyn Instance ID</FormLabel>
                <Input
                  value={formData.tenant_id}
                  onChange={e => setFormData(f => ({ ...f, tenant_id: e.target.value }))}
                  placeholder="e.g. my-organisation"
                  isReadOnly={!!editId}
                  bg={editId ? 'gray.50' : undefined}
                />
                <FormHelperText>
                  The Selwyn instance this config belongs to. Cannot be changed after creation.
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Microsoft Entra Directory (Tenant) ID</FormLabel>
                <Input
                  value={formData.entra_tenant_id}
                  onChange={e => setFormData(f => ({ ...f, entra_tenant_id: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  fontFamily="mono"
                />
                <FormHelperText>
                  Found in Azure Portal → App registrations → your app → Overview.
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Application (Client) ID</FormLabel>
                <Input
                  value={formData.entra_client_id}
                  onChange={e => setFormData(f => ({ ...f, entra_client_id: e.target.value }))}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  fontFamily="mono"
                />
              </FormControl>

              <FormControl isRequired={!editId}>
                <FormLabel>Client Secret</FormLabel>
                <InputGroup>
                  <Input
                    type={showSecret ? 'text' : 'password'}
                    value={formData.entra_client_secret}
                    onChange={e => setFormData(f => ({ ...f, entra_client_secret: e.target.value }))}
                    placeholder={editId ? 'Leave blank to keep current secret' : 'Paste secret value'}
                    fontFamily="mono"
                  />
                  <InputRightElement>
                    <IconButton
                      icon={showSecret ? <FiEyeOff /> : <FiEye />}
                      size="sm" variant="ghost"
                      onClick={() => setShowSecret(s => !s)}
                      aria-label="Toggle secret visibility"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormHelperText>
                  Azure Portal → App registrations → Certificates & secrets. Stored encrypted.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Allowed Email Domains</FormLabel>
                <HStack mb={2}>
                  <Input
                    value={domainInput}
                    onChange={e => setDomainInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addDomain())}
                    placeholder="acme.com"
                    size="sm"
                  />
                  <Button size="sm" colorScheme="teal" onClick={addDomain} leftIcon={<FiPlus />}>
                    Add
                  </Button>
                </HStack>
                <Wrap>
                  {formData.allowed_domains.map(d => (
                    <WrapItem key={d}>
                      <Tag colorScheme="teal" borderRadius="full">
                        <TagLabel>{d}</TagLabel>
                        <TagCloseButton onClick={() => removeDomain(d)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
                <FormHelperText>
                  Users with these email domains will be redirected to Microsoft SSO login.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <Flex align="center" justify="space-between">
                  <FormLabel mb={0}>Enable SSO for this tenant</FormLabel>
                  <Switch
                    colorScheme="green"
                    isChecked={formData.enabled}
                    onChange={e => setFormData(f => ({ ...f, enabled: e.target.checked }))}
                  />
                </Flex>
                <FormHelperText>
                  Only enabled tenants will have SSO active. Use this to test config before activating.
                </FormHelperText>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" isLoading={saving} onClick={handleSave}>
              {editId ? 'Save Changes' : 'Create Config'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Config</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete the config for{' '}
              <strong>{deleteTarget?.tenant_id}</strong>? This will disable SSO for this tenant.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
